import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import {
  listCategories,
  startExport,
  startImport,
  transferLimits,
  uploadPackage,
} from '../../api/business/endpoints';
import { useAppMutation } from '../../api/useAppMutation';
import { flattenCategories } from '../../components/categoryTree';
import { ErrorNotice } from '../../components/ErrorNotice';
import { packageProblem } from '../../components/transfers';
import { useT } from '../../i18n/LanguageContext';

/**
 * The two ways a transfer starts (R0037 Q-06): an export — every product or a category with everything under it, with
 * photos and files or without — and an import, whose ZIP is uploaded through the API (up to the deployment's limit,
 * which the API tells — R0042 — with its progress; R0041) and is then checked in the background.
 */
export const TransferStartCards = ({
  companyId,
  onStarted,
}: {
  companyId: string;
  onStarted: (transferId: string) => void;
}) => {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [scope, setScope] = useState<'all' | 'category'>('all');
  const [categoryId, setCategoryId] = useState('');
  const [includeMedia, setIncludeMedia] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const limits = useQuery({
    queryKey: ['transfer-limits', companyId],
    queryFn: () => transferLimits(companyId),
  });
  const maxMb: number | null = limits.data?.maxPackageMb ?? null;
  const categories = useQuery({
    queryKey: ['categories', companyId],
    queryFn: () => listCategories(companyId),
  });
  const exporting = useAppMutation(
    () =>
      startExport(companyId, {
        scope: scope === 'all' ? { all: true } : { categoryId },
        includeMedia,
      }),
    ['transfers'],
    (transfer) => onStarted(transfer.id),
  );
  const importing = useAppMutation(
    async (chosen: File) => {
      const transfer = await startImport(companyId, { fileName: chosen.name, bytes: chosen.size });

      onStarted(transfer.id);

      return uploadPackage(companyId, transfer.id, chosen, setProgress);
    },
    ['transfers', 'transfer'],
    () => {
      setProgress(null);
      setFile(null);
    },
  );

  const choose = (chosen: File | undefined): void => {
    if (chosen === undefined) return;

    const refusal = packageProblem(chosen, maxMb);

    setProblem(refusal === null ? null : t(refusal, { maxMb: maxMb ?? '' }));
    setFile(refusal === null ? chosen : null);
  };

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mb: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('transfers.exportTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('transfers.exportHint')}
          </Typography>
          <ErrorNotice error={exporting.error} />
          <RadioGroup value={scope} onChange={(event) => setScope(event.target.value as 'all' | 'category')}>
            <FormControlLabel value="all" control={<Radio size="small" />} label={t('transfers.scopeAll')} />
            <FormControlLabel
              value="category"
              control={<Radio size="small" />}
              label={t('transfers.scopeCategory')}
            />
          </RadioGroup>
          {scope === 'category' ? (
            <TextField
              select
              size="small"
              fullWidth
              label={t('transfers.category')}
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              sx={{ my: 1 }}
            >
              {flattenCategories(categories.data ?? []).map(({ category, path }) => (
                <MenuItem key={category.id} value={category.id}>
                  {path.join(' › ')}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <FormControlLabel
            control={
              <Checkbox checked={includeMedia} onChange={(event) => setIncludeMedia(event.target.checked)} />
            }
            label={t('transfers.includeMedia')}
          />
          <Stack direction="row" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              disabled={exporting.isPending || (scope === 'category' && categoryId === '')}
              onClick={() => exporting.mutate(undefined)}
            >
              {t('transfers.exportStart')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('transfers.importTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {maxMb === null ? null : t('transfers.importHint', { maxMb })}
          </Typography>
          <ErrorNotice error={importing.error} />
          {problem !== null ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {problem}
            </Alert>
          ) : null}
          <input
            ref={input}
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={(event) => {
              choose(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button variant="outlined" disabled={importing.isPending} onClick={() => input.current?.click()}>
              {t('transfers.chooseFile')}
            </Button>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {file?.name ?? t('transfers.noFile')}
            </Typography>
          </Stack>
          {progress !== null ? (
            <Box sx={{ my: 1 }}>
              <LinearProgress variant="determinate" value={Math.round(progress * 100)} />
              <Typography variant="caption" color="text.secondary">
                {t('transfers.uploading', { percent: Math.round(progress * 100) })}
              </Typography>
            </Box>
          ) : null}
          <Button
            variant="contained"
            disabled={file === null || importing.isPending}
            onClick={() => file !== null && importing.mutate(file)}
          >
            {t('transfers.importStart')}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
